import React, {
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from 'react';
import { KeyTextField } from '@prismicio/client';

export interface State {
  displayCartFlyout: boolean;
  displayLoginModal: boolean;
  displayEmployeePricingModal: boolean;
  displayStockLevelModal: boolean;
  displayCartStockLevelModal: boolean;
  displayStoresModal: boolean;
  displayDeliveryMethodModal: boolean;
  displayTagInfoModal: boolean;
  redirectPath: string | null;
  tagInfoModalPath?: string;
  displayVideoModal: boolean;
  displayMarketplaceSellerModal: boolean;
  displayStoreInformationModal: boolean;
  videoId: KeyTextField;
  videoHeight?: number;
  videoWidth?: number;
  sellerName: string;
  stockModalFilter?: string | undefined;
}

export interface Actions {
  openCartFlyout: () => void;
  closeCartFlyout: () => void;
  openLoginModal: (redirectPath?: string) => void;
  closeLoginModal: () => void;
  openEmployeePricingModal: () => void;
  closeEmployeePricingModal: () => void;
  openStoresModal: () => void;
  closeStoresModal: () => void;
  openStockLevelModal: (filter?: string) => void;
  closeStockLevelModal: () => void;
  openCartStockLevelModal: () => void;
  closeCartStockLevelModal: () => void;
  openDeliveryMethodModal: () => void;
  closeDeliveryMethodModal: () => void;
  openTagInfoModal: ({ path }: { path?: string }) => void;
  closeTagInfoModal: () => void;
  openVideoModal: ({
    videoId,
    videoHeight,
    videoWidth,
  }: {
    videoId: KeyTextField;
    videoHeight?: number;
    videoWidth?: number;
  }) => void;
  closeVideoModal: () => void;
  openMarketplaceSellerModal: ({ sellerName }: { sellerName: string }) => void;
  closeMarketplaceSellerModal: () => void;
  openStoreInformationModal: () => void;
  closeStoreInformationModal: () => void;
}
export type GlobalUIState = State & Actions;

enum ActionType {
  OPEN_CART_FLYOUT = 'OPEN_CART_FLYOUT',
  CLOSE_CART_FLYOUT = 'CLOSE_CART_FLYOUT',
  SHOW_COOKIE_CONSENT_BANNER = 'SHOW_COOKIE_CONSENT_BANNER',
  HIDE_COOKIE_CONSENT_BANNER = 'HIDE_COOKIE_CONSENT_BANNER',
  OPEN_LOGIN_MODAL = 'OPEN_LOGIN_MODAL',
  CLOSE_LOGIN_MODAL = 'CLOSE_LOGIN_MODAL',
  OPEN_EMPLOYEE_PRICING_MODAL = 'OPEN_EMPLOYEE_PRICING_MODAL',
  CLOSE_EMPLOYEE_PRICING_MODAL = 'CLOSE_EMPLOYEE_PRICING_MODAL',
  OPEN_STORES_MODAL = 'OPEN_STORES_MODAL',
  CLOSE_STORES_MODAL = 'CLOSE_STORES_MODAL',
  OPEN_STOCK_LEVEL_MODAL = 'OPEN_STOCK_LEVEL_MODAL',
  CLOSE_STOCK_LEVEL_MODAL = 'CLOSE_STOCK_LEVEL_MODAL',
  OPEN_CART_STOCK_LEVEL_MODAL = 'OPEN_CART_STOCK_LEVEL_MODAL',
  CLOSE_CART_STOCK_LEVEL_MODAL = 'CLOSE_CART_STOCK_LEVEL_MODAL',
  OPEN_DELIVERY_METHOD_MODAL = 'OPEN_DELIVERY_METHOD_MODAL',
  CLOSE_DELIVERY_METHOD_MODAL = 'CLOSE_DELIVERY_METHOD_MODAL',
  OPEN_TAG_INFO_MODAL = 'OPEN_TAG_INFO_MODAL',
  CLOSE_TAG_INFO_MODAL = 'CLOSE_TAG_INFO_MODAL',
  OPEN_VIDEO_MODAL = 'OPEN_VIDEO_MODAL',
  CLOSE_VIDEO_MODAL = 'CLOSE_VIDEO_MODAL',
  OPEN_MARKETPLACE_SELLER_MODAL = 'OPEN_MARKETPLACE_SELLER_MODAL',
  CLOSE_MARKETPLACE_SELLER_MODAL = 'CLOSE_MARKETPLACE_SELLER_MODAL',
  OPEN_STORE_INFORMATION_MODAL = 'OPEN_STORE_INFORMATION_MODAL',
  CLOSE_STORE_INFORMATION_MODAL = 'CLOSE_STORE_INFORMATION_MODAL',
}

type Action =
  | { type: 'OPEN_CART_FLYOUT' }
  | { type: 'CLOSE_CART_FLYOUT' }
  | { type: 'OPEN_LOGIN_MODAL'; redirectPath?: string }
  | { type: 'CLOSE_LOGIN_MODAL' }
  | { type: 'OPEN_EMPLOYEE_PRICING_MODAL' }
  | { type: 'CLOSE_EMPLOYEE_PRICING_MODAL' }
  | { type: 'OPEN_STORES_MODAL' }
  | { type: 'CLOSE_STORES_MODAL' }
  | { type: 'OPEN_STOCK_LEVEL_MODAL'; filter?: string }
  | { type: 'CLOSE_STOCK_LEVEL_MODAL' }
  | { type: 'OPEN_CART_STOCK_LEVEL_MODAL' }
  | { type: 'CLOSE_CART_STOCK_LEVEL_MODAL' }
  | { type: 'OPEN_DELIVERY_METHOD_MODAL' }
  | { type: 'CLOSE_DELIVERY_METHOD_MODAL' }
  | { type: 'CLOSE_TAG_INFO_MODAL' }
  | { type: 'OPEN_TAG_INFO_MODAL'; path?: string }
  | {
      type: 'OPEN_VIDEO_MODAL';
      videoId: KeyTextField;
      videoHeight?: number;
      videoWidth?: number;
    }
  | { type: 'CLOSE_VIDEO_MODAL' }
  | { type: 'OPEN_MARKETPLACE_SELLER_MODAL'; sellerName: string }
  | { type: 'CLOSE_MARKETPLACE_SELLER_MODAL' }
  | { type: 'OPEN_STORE_INFORMATION_MODAL' }
  | { type: 'CLOSE_STORE_INFORMATION_MODAL' };

const initialState: GlobalUIState = {
  displayCartFlyout: false,
  displayLoginModal: false,
  displayEmployeePricingModal: false,
  displayStockLevelModal: false,
  displayStoresModal: false,
  displayCartStockLevelModal: false,
  displayDeliveryMethodModal: false,
  displayTagInfoModal: false,
  displayMarketplaceSellerModal: false,
  displayStoreInformationModal: false,
  redirectPath: null,
  displayVideoModal: false,
  videoId: null,
  videoHeight: undefined,
  videoWidth: undefined,
  sellerName: '',
  stockModalFilter: undefined,
  openCartFlyout: () => {},
  closeCartFlyout: () => {},
  openLoginModal: () => {},
  closeLoginModal: () => {},
  openEmployeePricingModal: () => {},
  closeEmployeePricingModal: () => {},
  openStoresModal: () => {},
  closeStoresModal: () => {},
  openStockLevelModal: (filter?: string | undefined) => {},
  closeStockLevelModal: () => {},
  openCartStockLevelModal: () => {},
  closeCartStockLevelModal: () => {},
  openDeliveryMethodModal: () => {},
  closeDeliveryMethodModal: () => {},
  openTagInfoModal: ({ path }: { path?: string }) => {},
  closeTagInfoModal: () => {},
  openVideoModal: ({
    videoId,
    videoHeight,
    videoWidth,
  }: {
    videoId: KeyTextField;
    videoHeight?: number;
    videoWidth?: number;
  }) => {},
  closeVideoModal: () => {},
  openMarketplaceSellerModal: ({ sellerName }: { sellerName: string }) => {},
  closeMarketplaceSellerModal: () => {},
  openStoreInformationModal: () => {},
  closeStoreInformationModal: () => {},
};

export const UIContext = createContext<GlobalUIState>(initialState);
UIContext.displayName = 'UIContext';

const uiReducer = (state: State, action: Action) => {
  switch (action.type) {
    case ActionType.OPEN_CART_FLYOUT: {
      return {
        ...state,
        displayCartFlyout: true,
      };
    }
    case ActionType.CLOSE_CART_FLYOUT: {
      return {
        ...state,
        displayCartFlyout: false,
      };
    }
    case ActionType.OPEN_LOGIN_MODAL: {
      return {
        ...state,
        displayLoginModal: true,
        redirectPath: action.redirectPath ?? null,
      };
    }
    case ActionType.CLOSE_LOGIN_MODAL: {
      return {
        ...state,
        displayLoginModal: false,
        redirectPath: null,
      };
    }
    case ActionType.OPEN_EMPLOYEE_PRICING_MODAL: {
      return {
        ...state,
        displayEmployeePricingModal: true,
      };
    }
    case ActionType.CLOSE_EMPLOYEE_PRICING_MODAL: {
      window?.location.reload();
      return {
        ...state,
        displayEmployeePricingModal: false,
      };
    }
    case ActionType.OPEN_STORES_MODAL: {
      return {
        ...state,
        displayStoresModal: true,
      };
    }
    case ActionType.CLOSE_STORES_MODAL: {
      return {
        ...state,
        displayStoresModal: false,
      };
    }
    case ActionType.OPEN_STOCK_LEVEL_MODAL: {
      return {
        ...state,
        displayStockLevelModal: true,
        stockModalFilter: action.filter,
      };
    }
    case ActionType.CLOSE_STOCK_LEVEL_MODAL: {
      return {
        ...state,
        displayStockLevelModal: false,
      };
    }
    case ActionType.OPEN_CART_STOCK_LEVEL_MODAL: {
      return {
        ...state,
        displayCartStockLevelModal: true,
      };
    }
    case ActionType.CLOSE_CART_STOCK_LEVEL_MODAL: {
      return {
        ...state,
        displayCartStockLevelModal: false,
      };
    }
    case ActionType.OPEN_DELIVERY_METHOD_MODAL: {
      return {
        ...state,
        displayDeliveryMethodModal: true,
      };
    }
    case ActionType.CLOSE_DELIVERY_METHOD_MODAL: {
      return {
        ...state,
        displayDeliveryMethodModal: false,
      };
    }
    case ActionType.OPEN_TAG_INFO_MODAL: {
      return {
        ...state,
        displayTagInfoModal: true,
        tagInfoModalPath: action.path,
      };
    }
    case ActionType.CLOSE_TAG_INFO_MODAL: {
      return {
        ...state,
        displayTagInfoModal: false,
      };
    }
    case ActionType.OPEN_VIDEO_MODAL: {
      return {
        ...state,
        displayVideoModal: true,
        videoId: action.videoId,
        videoHeight: action.videoHeight ?? 0,
        videoWidth: action.videoWidth ?? 0,
      };
    }
    case ActionType.CLOSE_VIDEO_MODAL: {
      return {
        ...state,
        displayVideoModal: false,
      };
    }
    case ActionType.OPEN_MARKETPLACE_SELLER_MODAL: {
      return {
        ...state,
        displayMarketplaceSellerModal: true,
        sellerName: action.sellerName,
      };
    }
    case ActionType.CLOSE_MARKETPLACE_SELLER_MODAL: {
      return {
        ...state,
        displayMarketplaceSellerModal: false,
      };
    }
    case ActionType.OPEN_STORE_INFORMATION_MODAL: {
      return {
        ...state,
        displayStoreInformationModal: true,
      };
    }
    case ActionType.CLOSE_STORE_INFORMATION_MODAL: {
      return {
        ...state,
        displayStoreInformationModal: false,
      };
    }
    default: {
      return { ...state };
    }
  }
};

interface GlobalUIContextProviderProps {
  children: ReactNode;
}

const UIProvider = (props: GlobalUIContextProviderProps) => {
  const [state, dispatch] = useReducer(uiReducer, initialState);

  const openCartFlyout = () => dispatch({ type: ActionType.OPEN_CART_FLYOUT });

  const closeCartFlyout = () =>
    dispatch({ type: ActionType.CLOSE_CART_FLYOUT });

  const openLoginModal = useCallback(
    (redirectPath?: string) =>
      dispatch({
        type: ActionType.OPEN_LOGIN_MODAL,
        redirectPath,
      }),
    []
  );

  const closeLoginModal = () =>
    dispatch({ type: ActionType.CLOSE_LOGIN_MODAL });

  const openStockLevelModal = (filter?: string | undefined) =>
    dispatch({ type: ActionType.OPEN_STOCK_LEVEL_MODAL, filter });

  const closeStockLevelModal = () =>
    dispatch({ type: ActionType.CLOSE_STOCK_LEVEL_MODAL });

  const openCartStockLevelModal = () =>
    dispatch({ type: ActionType.OPEN_CART_STOCK_LEVEL_MODAL });

  const closeCartStockLevelModal = () =>
    dispatch({ type: ActionType.CLOSE_CART_STOCK_LEVEL_MODAL });

  const openEmployeePricingModal = () =>
    dispatch({ type: ActionType.OPEN_EMPLOYEE_PRICING_MODAL });

  const closeEmployeePricingModal = () =>
    dispatch({ type: ActionType.CLOSE_EMPLOYEE_PRICING_MODAL });

  const openStoresModal = () =>
    dispatch({ type: ActionType.OPEN_STORES_MODAL });

  const closeStoresModal = () =>
    dispatch({ type: ActionType.CLOSE_STORES_MODAL });

  const openDeliveryMethodModal = () =>
    dispatch({ type: ActionType.OPEN_DELIVERY_METHOD_MODAL });

  const closeDeliveryMethodModal = () =>
    dispatch({ type: ActionType.CLOSE_DELIVERY_METHOD_MODAL });

  const openTagInfoModal = useCallback(({ path }: { path?: string }) => {
    dispatch({ type: ActionType.OPEN_TAG_INFO_MODAL, path });
  }, []);

  const closeTagInfoModal = () =>
    dispatch({ type: ActionType.CLOSE_TAG_INFO_MODAL });

  const openVideoModal = ({
    videoId,
    videoHeight,
    videoWidth,
  }: {
    videoId: KeyTextField;
    videoHeight?: number;
    videoWidth?: number;
  }) =>
    dispatch({
      type: ActionType.OPEN_VIDEO_MODAL,
      videoId,
      videoHeight,
      videoWidth,
    });
  const closeVideoModal = () =>
    dispatch({ type: ActionType.CLOSE_VIDEO_MODAL });

  const openMarketplaceSellerModal = ({ sellerName }: { sellerName: string }) =>
    dispatch({ type: ActionType.OPEN_MARKETPLACE_SELLER_MODAL, sellerName });

  const closeMarketplaceSellerModal = () =>
    dispatch({ type: ActionType.CLOSE_MARKETPLACE_SELLER_MODAL });

  const openStoreInformationModal = () =>
    dispatch({ type: ActionType.OPEN_STORE_INFORMATION_MODAL });

  const closeStoreInformationModal = () =>
    dispatch({ type: ActionType.CLOSE_STORE_INFORMATION_MODAL });

  const value: GlobalUIState = useMemo(
    () => ({
      ...state,
      openCartFlyout,
      closeCartFlyout,
      openLoginModal,
      closeLoginModal,
      openEmployeePricingModal,
      closeEmployeePricingModal,
      openStoresModal,
      closeStoresModal,
      openStockLevelModal,
      closeStockLevelModal,
      openCartStockLevelModal,
      closeCartStockLevelModal,
      openDeliveryMethodModal,
      closeDeliveryMethodModal,
      openTagInfoModal,
      closeTagInfoModal,
      openVideoModal,
      closeVideoModal,
      openMarketplaceSellerModal,
      closeMarketplaceSellerModal,
      openStoreInformationModal,
      closeStoreInformationModal,
    }),
    [state, openLoginModal, openTagInfoModal]
  );

  return <UIContext.Provider value={value} {...props} />;
};

export const GlobalUIContext = ({ children }: { children: ReactNode }) => (
  <UIProvider>{children}</UIProvider>
);

export const useUI = () => {
  const context = useContext(UIContext);
  if (context === undefined) {
    throw new Error(`useUI must be used within a UIProvider`);
  }
  return context;
};
